import React, { useCallback, useEffect, useState } from 'react';

import {
    DeactivateContractsInput,
    GetContractDealsByIdsInput,
    GetContractDealsByIdsOutput,
    IntegerList
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import plusCircleTokens from '@amzn/meridian-tokens/base/icon/plus-circle';
import deleteTokens from '@amzn/meridian-tokens/base/icon/trash';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { ColDef, ColumnState, GridApi, ICellRendererParams } from 'ag-grid-community';
import { Link, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BaseAgGrid } from 'src/components/BaseAgGrid';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { FilterInput } from 'src/components/FilterInput';
import { ProtectedRoute } from 'src/components/ProtectedRoute';
import { ValidateNewRateCardModal } from 'src/components/RateCards/ValidateNewRateCardModal/ValidateNewRateCardModal';
import { DEFAULT_TOASTER_TIMEOUT, RateCardStatus } from 'src/constants';
import { EXCEL_DATE_STYLE_ID, LINK_COLOR } from 'src/constants/agGrid';
import { EDITOR_ROLE_SET, VIEWER_ROLE_SET } from 'src/constants/role';
import { ROUTES, URL_PARAMS } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IRateCardsRowData } from 'src/types/IRateCard';
import { IToast } from 'src/types/IToaster';
import { emptyCellFormatter } from 'src/util/grid/cellFormatter';
import { getStatusForRateCard, getYYYYMMDDStrFromTimestamp } from 'src/util/grid/cellGetter';
import { getPhoneToolLinkCellRenderer, getStatusCellRenderer } from 'src/util/grid/cellRenderer';
import { dateFilterOptions } from 'src/util/grid/filter';
import { dateComparator, dateFilterComparator } from 'src/util/grid/sort';

import { RateCardRouteWrapper } from '../RateCardRouteWrapper';
import { AddRateCardModal } from './AddRateCardModal/AddRateCardModal';
import './RateCards.scss';

export const RateCards = () => {
    const { path } = useRouteMatch();
    const { pathname } = useLocation();
    const history = useHistory();
    const { setTopAlertMsg, userRole, setToasts, userInfo } = React.useContext(AppContext);

    const [rateCardsRowData, setRateCardsRowData] = useState<IRateCardsRowData[]>([]);
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [filterText, setFilterText] = useState('');
    const [isDeleteRateCardModalOpen, setIsDeleteRateCardModalOpen] = useState(false);
    const [contractIdToDelete, setContractIdToDelete] = useState<number | null>(null);
    const [isAddRateCardModalOpen, setIsAddRateCardModalOpen] = useState(false);
    const [agGridApi, setAgGridApi] = useState<GridApi>();
    const [newContractId, setNewContractId] = useState<number | undefined>();
    const [templateContractId, setTemplateContractId] = useState<number | undefined>();

    useEffect(() => {
        if (pathname !== ROUTES.RATE_CARDS.INDEX) return;

        contractConfigurationClient
            .getContractDealsByIds(
                GetContractDealsByIdsInput.clone({
                    includeDeletedContracts: true,
                    includeDraftContracts: true,
                    includeIneligibleContractDeals: true
                })
            )
            .subscribe(
                (response: GetContractDealsByIdsOutput) => {
                    setIsDataLoaded(true);
                    setRateCardsRowData(response.contractDeals ? response.contractDeals : []);
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('getContractDealsByIds unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                }
            );
    }, [pathname]);

    const rateCardsColDefs: ColDef[] = [
        {
            field: 'contractName',
            headerName: 'Contract',
            filter: 'agSetColumnFilter',
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Link
                        style={{ color: LINK_COLOR, textDecoration: 'none' }}
                        to={`${ROUTES.RATE_CARDS.INDEX}/${params.data.contractId}`}
                    >
                        {params.value}
                    </Link>
                );
            },
            width: 400,
            minWidth: 200
        },
        {
            field: 'serviceName',
            headerName: 'Service',
            filter: 'agSetColumnFilter',
            minWidth: 120
        },
        {
            field: 'contractChangedDate',
            headerName: 'Last updated',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'greaterThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: EXCEL_DATE_STYLE_ID,
            valueFormatter: emptyCellFormatter,
            valueGetter: getYYYYMMDDStrFromTimestamp,
            minWidth: 150
        },
        {
            field: 'contractChangedBy',
            headerName: 'Last updated By',
            filter: 'agSetColumnFilter',
            cellRenderer: getPhoneToolLinkCellRenderer,
            minWidth: 180
        },
        {
            field: 'contractCreationDate',
            headerName: 'Published',
            comparator: dateComparator,
            filter: 'agDateColumnFilter',
            filterParams: {
                filterOptions: dateFilterOptions,
                defaultOption: 'greaterThanOrEqual',
                comparator: dateFilterComparator,
                includeBlanksInLessThan: true,
                includeBlanksInGreaterThan: true
            },
            cellClass: EXCEL_DATE_STYLE_ID,
            valueGetter: getYYYYMMDDStrFromTimestamp,
            minWidth: 120
        },
        {
            field: 'contractCreatedBy',
            headerName: 'Published by',
            comparator: dateComparator,
            filter: 'agSetColumnFilter',
            cellRenderer: getPhoneToolLinkCellRenderer,
            minWidth: 150
        },
        {
            field: 'status',
            headerName: 'Status',
            valueGetter: getStatusForRateCard,
            cellRenderer: getStatusCellRenderer,
            cellRendererParams: {
                displayText: true
            },
            minWidth: 120
        },
        {
            field: 'action',
            headerName: 'Actions',
            filter: false,
            cellRenderer: (params: ICellRendererParams) => {
                const editPath = `${ROUTES.RATE_CARDS.INDEX}${ROUTES.RATE_CARDS.EDIT}/${params.data.contractId}`;
                return (
                    <Column height="100%" alignmentVertical="center">
                        <Row>
                            <Button
                                type="link"
                                size="small"
                                href={editPath}
                                onClick={history.push}
                                disabled={!EDITOR_ROLE_SET.has(userRole)}
                            >
                                <Icon className="button-icon" tokens={editTokens} />
                            </Button>
                            {params.data.contractIsDraft && !params.data.contractIsDeleted && (
                                <Button
                                    type="link"
                                    size="small"
                                    onClick={() => handleRateCardDeleteBtnClick(params.data.contractId)}
                                    disabled={!EDITOR_ROLE_SET.has(userRole)}
                                    data-testid="delete-rate-card-btn"
                                >
                                    <Icon className="button-icon" tokens={deleteTokens} />
                                </Button>
                            )}
                        </Row>
                    </Column>
                );
            },
            minWidth: 120
        }
    ];

    const rateCardsColSortModel: ColumnState[] = [
        { colId: 'contractChangedDate', sort: 'desc', sortIndex: 0 },
        { colId: 'contractName', sort: 'asc', sortIndex: 1 }
    ];

    const handleRateCardDeleteBtnClick = (contractId: number) => {
        setContractIdToDelete(contractId);
        setIsDeleteRateCardModalOpen(true);
    };

    const handleRateCardDeleteWithToast = () => {
        if (contractIdToDelete === null) return;

        contractConfigurationClient
            .deactivateContracts(
                DeactivateContractsInput.clone({
                    changeBy: userInfo.username,
                    contractIds: [contractIdToDelete] as IntegerList
                })
            )
            .subscribe(
                () => {
                    setIsDataLoaded(false);

                    contractConfigurationClient
                        .getContractDealsByIds(
                            GetContractDealsByIdsInput.clone({
                                includeDeletedContracts: true,
                                includeDraftContracts: true,
                                includeIneligibleContractDeals: true
                            })
                        )
                        .subscribe(
                            (response: GetContractDealsByIdsOutput) => {
                                setIsDataLoaded(true);

                                const toastMessage = `Draft rate card deleted`;
                                const toast: IToast = {
                                    id: 'delete-rate-card-toast',
                                    timeout: DEFAULT_TOASTER_TIMEOUT * 2,
                                    message: toastMessage
                                };

                                setToasts([toast]);
                                setIsDeleteRateCardModalOpen(false);
                                setContractIdToDelete(null);

                                setRateCardsRowData(response.contractDeals ? response.contractDeals : []);
                            },
                            (error: UnknownCoralError) => {
                                // eslint-disable-next-line no-console
                                console.error(
                                    'GetContractDealsByIds Unexpected error',
                                    error.receivedException.message
                                );
                                setTopAlertMsg(error.receivedException.message);
                                setContractIdToDelete(null);
                            }
                        );
                },
                (error: UnknownCoralError) => {
                    // eslint-disable-next-line no-console
                    console.error('DeactivateContractDeals Unexpected error', error.receivedException.message);
                    setTopAlertMsg(error.receivedException.message);
                    setContractIdToDelete(null);
                }
            );
    };

    const setDefaultFilter = () => {
        const statusFilterModel = {
            status: {
                filterType: 'set',
                values: [RateCardStatus.Active]
            }
        };
        agGridApi?.setFilterModel(statusFilterModel);
    };

    useEffect(() => {
        if (agGridApi) setDefaultFilter();
    }, [agGridApi]);

    const rateCardsContent = (
        <Column className="rate-cards-div" spacing="300" height="100%" width="100%" minHeight={480}>
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <Button
                        type="link"
                        size="small"
                        onClick={() => setIsAddRateCardModalOpen(true)}
                        disabled={!EDITOR_ROLE_SET.has(userRole)}
                    >
                        <Icon className="button-icon" tokens={plusCircleTokens} />
                        Add contract
                    </Button>
                </Row>
            </Box>
            <div className="rate-cards-grid">
                <BaseAgGrid
                    columnDefs={rateCardsColDefs}
                    sortModel={rateCardsColSortModel}
                    rowData={rateCardsRowData}
                    filterText={filterText}
                    setAgGridApi={setAgGridApi}
                    onFirstDataRendered={setDefaultFilter}
                    loading={!isDataLoaded}
                />
            </div>

            <ConfirmationModal
                isOpen={isDeleteRateCardModalOpen}
                onClose={() => setIsDeleteRateCardModalOpen(false)}
                title="Confirm delete rate card"
                body="Are you sure you want to delete draft rate card?"
                showComments={false}
                primaryButtonText="Yes, delete rate card"
                primaryButtonOnClick={handleRateCardDeleteWithToast}
                secondaryButtonText="No, do not delete"
                secondaryButtonOnClick={() => setIsDeleteRateCardModalOpen(false)}
            />

            <AddRateCardModal
                isAddRateCardModalOpen={isAddRateCardModalOpen}
                setIsAddRateCardModalOpen={setIsAddRateCardModalOpen}
                rateCardsRowData={rateCardsRowData}
                setTemplateContractId={setTemplateContractId}
                setNewContractId={setNewContractId}
            />

            <ValidateNewRateCardModal
                isAddRateCardModalOpen={isAddRateCardModalOpen}
                newContractId={newContractId}
                setNewContractId={setNewContractId}
                templateContractId={templateContractId}
                setTemplateContractId={setTemplateContractId}
            />
        </Column>
    );

    return (
        <div className="rate-cards-container">
            <Switch>
                <ProtectedRoute exact requiredRoleSet={VIEWER_ROLE_SET} path={path}>
                    {rateCardsContent}
                </ProtectedRoute>
                <ProtectedRoute
                    requiredRoleSet={EDITOR_ROLE_SET}
                    path={`${path}${URL_PARAMS.EDIT_TYPE}${URL_PARAMS.RATE_CARD_ID}`}
                    key={path}
                >
                    <RateCardRouteWrapper />
                </ProtectedRoute>
                <ProtectedRoute requiredRoleSet={VIEWER_ROLE_SET} path={`${path}${URL_PARAMS.RATE_CARD_ID}`}>
                    <RateCardRouteWrapper />
                </ProtectedRoute>
            </Switch>
        </div>
    );
};
